/**
 * Slot Check Search map camera helper (no mapbox import, so it is unit-tested).
 */
type Padding = { top: number; bottom: number; left: number; right: number };
/** The Mapbox map methods fitOverview uses. */
export type FitMap = {
  stop: () => unknown;
  setPadding: (p: Padding) => unknown;
  getContainer: () => { clientWidth: number; clientHeight: number } | null | undefined;
  fitBounds: (b: [[number, number], [number, number]], o: { padding: Padding | number; maxZoom: number; duration: number }) => unknown;
  jumpTo: (o: { center: [number, number]; zoom: number }) => unknown;
};

export type SafeFitResult = 'fit' | 'fit-nopad' | 'fallback';

/**
 * Fit the map to bounds without ever throwing. A throw inside a React effect
 * unmounts the whole page (the gray screen after clearing a search with the chip ✕).
 *
 * - **Padding reset first.** Mapbox keeps camera padding from an earlier
 *   flyTo/easeTo, and fitBounds stacks it with its own padding. The box left
 *   for the bounds goes negative and Mapbox throws
 *   "Invalid LngLat object: (NaN, NaN)".
 * - **Padded fit** (search bar on top, drawer at the bottom), only when it
 *   leaves room in the canvas.
 * - **Then a fit with no padding**, then `fallback` (jumpTo).
 */
export function safeFitBounds(
  map: FitMap,
  bounds: [[number, number], [number, number]],
  pad: { top: number; bottom: number },
  opts: { maxZoom: number; duration?: number; fallback: { lng: number; lat: number; zoom: number } }
): SafeFitResult {
  try {
    map.stop();
    map.setPadding({ top: 0, bottom: 0, left: 0, right: 0 });
  } catch (e) {
    // ignore
  }
  const el = map.getContainer();
  const w = el?.clientWidth || 0;
  const h = el?.clientHeight || 0;
  const padding = { top: Math.max(0, pad.top) + 40, left: 40, right: 40, bottom: Math.max(0, pad.bottom) + 40 };
  const attempts: Array<['fit' | 'fit-nopad', Padding | number]> = [];
  if (w - padding.left - padding.right > 40 && h - padding.top - padding.bottom > 40) attempts.push(['fit', padding]);
  attempts.push(['fit-nopad', 0]);
  for (const [label, p] of attempts) {
    try {
      map.fitBounds(bounds, { padding: p, maxZoom: opts.maxZoom, duration: opts.duration ?? 0 });
      return label;
    } catch (e) {
      // try the next option
    }
  }
  try {
    map.jumpTo({ center: [opts.fallback.lng, opts.fallback.lat], zoom: opts.fallback.zoom });
  } catch (e) {
    // ignore
  }
  return 'fallback';
}

/** Overview (no search): fit around every casino; falls back to the world view. */
export function fitOverview(
  map: FitMap,
  bounds: [[number, number], [number, number]],
  pad: { top: number; bottom: number },
  opts: { maxZoom: number; world: { lng: number; lat: number; zoom: number } }
): 'fit' | 'fit-nopad' | 'world' {
  const r = safeFitBounds(map, bounds, pad, { maxZoom: opts.maxZoom, fallback: opts.world });
  return r === 'fallback' ? 'world' : r;
}
